// src/pages/ProjectBuilderPage.jsx
import React, { useEffect, useRef, useState } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";

/* ---------------------- Language display names ---------------------- */
const LANGUAGE_MAP = {
 en:"English", hi:"Hindi", ta:"Tamil", te:"Telugu", kn:"Kannada",
 ml:"Malayalam", mr:"Marathi", gu:"Gujarati", bn:"Bengali",
 pa:"Punjabi", ur:"Urdu",
};

/* ----------------------------- Helpers ------------------------------ */
async function fetchWithTimeout(url, options, timeout = 60000) {
 const controller = new AbortController();
 const id = setTimeout(() => controller.abort(), timeout);
 try {
 const res = await fetch(url, { ...options, signal: controller.signal });
 return res;
 } finally {
 clearTimeout(id);
 }
}

function classNames(...xs) {
 return xs.filter(Boolean).join("");
}

/* ----------------------------- UI bits ------------------------------ */
function SectionCard({ title, right, children }) {
  return (
    <div className="rounded-3xl border-2 border-[#5B21B6] bg-[#F9F7FF] p-6 shadow-xl shadow-[#5B21B6]/5 transition-all hover:shadow-[#5B21B6]/10">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-xl font-black text-primary uppercase tracking-tight">{title}</h3>
        {right}
      </div>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
}

function CodeTabs({ files }) {
 const [active, setActive] = useState(0);
 const list = Array.isArray(files) ? files : [];
 const file = list[active];

 const download = () => {
 if (!file) return;
 const blob = new Blob([file.code ||""], { type:"text/plain" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url; a.download = file.file ||"File.jsx"; a.click();
 URL.revokeObjectURL(url);
 };

 const copyToClipboard = async () => {
 if (!file) return;
 try {
 await navigator.clipboard.writeText(file.code ||"");
 } catch {
 // Fallback: silently ignore; copying is a nice-to-have
 }
 };

 return (
 <div className="mt-2">
 <div className="flex gap-2 flex-wrap items-center">
 {list.map((f, i) => (
 <button
 key={i}
 onClick={() => setActive(i)}
 className={classNames(
"px-2 py-1 text-xs rounded border",
 i === active ?"bg-primary text-white" :"border-primary text-primary"
 )}
 >
 {f.file}
 </button>
 ))}
 <div className="ml-auto flex items-center gap-2">
 {file && (
 <>
 <button onClick={copyToClipboard} className="px-2 py-1 text-xs border border-primary text-primary rounded">
 📋 Copy
 </button>
 <button onClick={download} className="px-2 py-1 text-xs bg-primary text-white rounded">
 ⬇️ Download
 </button>
 </>
 )}
 </div>
 </div>
 {file ? (
 <pre className="mt-2 p-3 bg-[#0a0f1b] border border-accentGray/50 rounded overflow-auto max-h-[50vh] text-sm">
{file.code}
 </pre>
 ) : (
 <div className="text-white/70 text-sm mt-2">No files.</div>
 )}
 </div>
 );
}

/* Paste / Drag & drop image zone */
function PasteDropZone({ images, setImages, autoFocus = false }) {
 const [hover, setHover] = useState(false);
 const inputRef = useRef(null);
 const zoneRef = useRef(null);

 const addFiles = (filesOrBlobs) => {
 const items = Array.from(filesOrBlobs || []);
 const toFiles = items.map((item, idx) => {
 if (item instanceof File) return item;
 const type = item.type ||"image/png";
 const name = `pasted-${Date.now()}-${idx}.${type.includes("jpeg") ?"jpg" : (type.split("/")[1] ||"png")}`;
 return new File([item], name, { type });
 });
 setImages([...(images || []), ...toFiles].slice(0, 6));
 };

 const handleClipboardEvent = (e) => {
 const items = e.clipboardData?.items || [];
 const blobs = [];
 for (const it of items) {
 if (it.kind ==="file" && (it.type ||"").startsWith("image/")) {
 const blob = it.getAsFile();
 if (blob) blobs.push(blob);
 }
 }
 if (blobs.length) { e.preventDefault(); addFiles(blobs); }
 };

 const onDrop = (e) => {
 e.preventDefault();
 e.stopPropagation();
 setHover(false);
 const dt = e.dataTransfer;
 if (dt?.files?.length) addFiles(dt.files);
 };

 useEffect(() => {
 const onWindowPaste = (e) => handleClipboardEvent(e);
 window.addEventListener("paste", onWindowPaste);
 return () => window.removeEventListener("paste", onWindowPaste);
 }, []);

 useEffect(() => {
 if (autoFocus && zoneRef.current) {
 const t = setTimeout(() => zoneRef.current?.focus(), 60);
 return () => clearTimeout(t);
 }
 }, [autoFocus]);

 return (
 <div
 ref={zoneRef}
 tabIndex={0}
 role="button"
 onPaste={handleClipboardEvent}
 onDrop={onDrop}
 onDragOver={(e) => { e.preventDefault(); setHover(true); }}
 onDragLeave={() => setHover(false)}
 onClick={() => inputRef.current?.click()}
 className={classNames(
"rounded border-2 p-3 cursor-pointer select-none outline-none",
 hover ?"border-primary bg-primary/10" :"border-primary/60 bg-[#0a0f1b]"
 )}
 title="Click / Paste (Ctrl/⌘+V / Win+V) / Drag & Drop images"
 >
 <div className="text-sm text-white/80">
 <div className="font-medium text-primary mb-1">Images</div>
 <div className="text-white/70">
 Paste <kbd>Ctrl/⌘+V</kbd> or <kbd>Win+V</kbd>, drag & drop, or click to choose.
 </div>
 </div>

 <input
 ref={inputRef}
 type="file"
 accept="image/*"
 multiple
 className="hidden"
 onChange={(e) => addFiles(e.target.files)}
 />

 {!!(images && images.length) && (
 <div className="mt-3 grid grid-cols-3 gap-2">
 {images.map((file, i) => {
 const url = URL.createObjectURL(file);
 return (
 <div key={i} className="relative group">
 <img
 src={url}
 alt={`img-${i}`}
 className="w-full h-24 object-cover rounded border border-accentGray/50"
 onLoad={() => URL.revokeObjectURL(url)}
 />
 <button
 className="absolute top-1 right-1 bg-bgMain/60 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100"
 onClick={(e) => {
 e.stopPropagation();
 setImages(images.filter((_, idx) => idx !== i));
 }}
 >
 ✕
 </button>
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}

/* Helper to download a data URL as a file */
function downloadDataUrl(name, dataUrl) {
 const a = document.createElement("a");
 a.href = dataUrl;
 a.download = name ||"asset.png";
 a.click();
}

/* =========================== Main Page ============================ */
export default function ProjectBuilderPage() {
 return (
  <FeatureGuard feature="TSG_AI">
    <ProjectBuilderPageContent />
  </FeatureGuard>
 );
}

function ProjectBuilderPageContent() {
 const [idea, setIdea] = useState("");
 const [loading, setLoading] = useState(false);
 const [plan, setPlan] = useState(null);
 const [raw, setRaw] = useState("");
 const [parseErr, setParseErr] = useState(false);
 const [languageMode, setLanguageMode] = useState(null); //"pure" |"mixed"
 const [projectId, setProjectId] = useState(null);
 const navigate = useNavigate();

 // Error banner for builder
 const [lastError, setLastError] = useState("");
 const [lastRequestId, setLastRequestId] = useState("");

 // Expert Help modal state
 const [helpOpen, setHelpOpen] = useState(false);
 const [helpMode, setHelpMode] = useState("build"); //"build" |"fix"
 const [uiRoute, setUiRoute] = useState("/page");
 const [uiDesc, setUiDesc] = useState("");
 const [uiIssues, setUiIssues] = useState("");
 const [uiExisting, setUiExisting] = useState("");
 const [uiFiles, setUiFiles] = useState([]);
 const [uiFidelity, setUiFidelity] = useState("high");
 const [uiAssetHints, setUiAssetHints] = useState("");
 const [uiPreserveClasses, setUiPreserveClasses] = useState(true);
 const [uiLoading, setUiLoading] = useState(false);
 const [uiResult, setUiResult] = useState(null);
 const [uiAssets, setUiAssets] = useState([]);
 const [uiAudit, setUiAudit] = useState([]); // <- separate so it renders even when result exists
 const [uiReqId, setUiReqId] = useState("");

 const location = useLocation();
 const { session } = useSkillGenieSession();
 const langCode = (location.state?.language || session?.language ||"en").toLowerCase();
 const langName = LANGUAGE_MAP[langCode] ||"English";
 const mode = (location.state?.languageMode || session?.languageMode ||"mixed");

 useEffect(() => setLanguageMode(mode), [mode]);

 const chatRef = useRef(null);
 useEffect(() => {
   const container = chatRef.current?.parentElement;
   if (container) {
     const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
     if (isAtBottom) {
       chatRef.current?.scrollIntoView({ behavior: "smooth" });
     }
   }
 }, [plan, raw, parseErr]);

 const handleBuild = async () => {
 if (!idea.trim()) return;
 setLoading(true);
 setParseErr(false);
 setRaw("");
 setLastError("");
 setLastRequestId("");

 const userId = JSON.parse(localStorage.getItem("user"))?.userId ||"guest";

 try {
 const res = await fetchWithTimeout(`${API_BASE_URL}/api/tsg/project_builder`, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({
 userId,
 description: idea,
 language: langCode,
 languageMode,
 chatHistory: [],
 domain:"",
 constraints:"",
 preferences:""
 })
 }, 30000);

 let data;
 try { data = await res.json(); } catch { data = {}; }

 if (!res.ok) {
 const msg = data?.error || res.statusText ||"Server error";
 setLastError(`Project Builder failed: ${msg}`);
 if (data?.requestId) setLastRequestId(data.requestId);
 setPlan(null);
 setProjectId(null);
 setRaw("");
 setParseErr(false);
 return;
 }

 setPlan(data.plan || null);
 setProjectId(data.projectId || null);
 setRaw(data.raw ||"");
 setParseErr(Boolean(data.parse_error));
 } catch (err) {
 // Distinguish aborts
 const aborted = err?.name ==="AbortError";
 setLastError(aborted ?"Timed out contacting Project Builder." : ("Network/unknown error:" + (err?.message || String(err))));
 setPlan(null);
 setProjectId(null);
 setRaw("❌ Error reaching Project Builder API.");
 setParseErr(true);
 } finally {
 setLoading(false);
 }
 };

 const downloadJSON = () => {
 if (!plan) return;
 const blob = new Blob([JSON.stringify(plan, null, 2)], { type:"application/json" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url; a.download ="project_plan.json"; a.click();
 URL.revokeObjectURL(url);
 };

 const resetAll = () => {
 setIdea("");
 setPlan(null);
 setProjectId(null);
 setRaw("");
 setParseErr(false);
 setLastError("");
 setLastRequestId("");
 };

 /* -------------------------- Expert Help -------------------------- */
 const canRunHelp =
 helpMode ==="build"
 ? (uiDesc.trim().length > 0 || uiFiles.length > 0)
 : (uiFiles.length > 0 && uiIssues.trim().length >= 10);

 const submitExpertHelp = async () => {
 if (!projectId) {
 setUiResult({ error:"No project yet. Generate a plan first." });
 setUiAudit([]);
 setUiReqId("");
 return;
 }
 if (helpMode ==="fix" && !canRunHelp) {
 setUiResult({ error:"Add at least one screenshot and describe the issue (≥ 10 characters)." });
 setUiAudit([]);
 setUiReqId("");
 return;
 }

 setUiLoading(true);
 setUiResult(null);
 setUiAssets([]);
 setUiAudit([]);
 setUiReqId("");

 try {
 const form = new FormData();
 const userId = JSON.parse(localStorage.getItem("user"))?.userId ||"guest";

 form.append("userId", userId);
 form.append("language", langCode);
 form.append("languageMode", languageMode ||"mixed");
 form.append("route", uiRoute);
 form.append("fidelity", uiFidelity);
 form.append("assetHints", uiAssetHints);

 if (helpMode ==="build") {
 form.append("description", uiDesc);
 } else {
 form.append("issues", uiIssues);
 form.append("existingCode", uiExisting);
 form.append("preserveClasses", String(uiPreserveClasses));
 form.append("requireEvaluation","true"); // ask server to include analysis
 }

 for (const f of uiFiles) form.append("images", f);

 const url =
 helpMode ==="build"
 ? `${API_BASE_URL}/api/tsg/project/${projectId}/ui/generate`
 : `${API_BASE_URL}/api/tsg/project/${projectId}/ui/fix`;

 const res = await fetchWithTimeout(url, { method:"POST", body: form }, 45000);

 let data;
 try { data = await res.json(); } catch { throw new Error("Invalid JSON from server. Check backend logs."); }

 if (!res.ok) {
 setUiResult({ error: data.error ||"Server error", pages: [], components: [], styles:"" });
 setUiAudit([]);
 setUiReqId(data.requestId ||"");
 setUiAssets([]);
 return;
 }

 setUiResult(data.result || null);
 setUiAssets(Array.isArray(data.assets) ? data.assets : []);
 setUiAudit(Array.isArray(data.audit) ? data.audit : []);
 setUiReqId(data.requestId ||"");
 } catch (e) {
 const aborted = e?.name ==="AbortError";
 setUiResult({ error: aborted ?"Timed out talking to UI service." : (e.message || String(e)), pages: [], components: [], styles:"" });
 setUiAudit([]);
 setUiReqId("");
 setUiAssets([]);
 } finally {
 setUiLoading(false);
 }
 };

  return (
    <div className="p-4 md:p-8 min-h-screen bg-[#F8FAFC] text-[#111827] font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
              title="Go Back"
            >
              <ChevronLeftIcon className="w-6 h-6 text-[#5B21B6] group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="p-3 bg-purple-50 rounded-xl border-2 border-[#5B21B6] shadow-sm">
              <svg className="w-8 h-8 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-black text-[#111827] uppercase tracking-tight">
                @tsg <span className="text-[#5B21B6]">→</span> Project Builder
              </h2>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Comprehensive AI-powered project architecture</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-purple-100 shadow-sm">
              <span className="text-[#5B21B6] text-sm">🗣️</span>
              <select
                value={languageMode || "mixed"}
                onChange={(e) => setLanguageMode(e.target.value)}
                className="bg-transparent text-gray-700 text-xs font-bold focus:outline-none uppercase tracking-tighter"
              >
                <option value="pure">Pure Language</option>
                <option value="mixed">Mixed (Tech Terms)</option>
              </select>
            </div>
            <button
              onClick={() => setHelpOpen(true)}
              className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl font-black border border-purple-100 transition-all flex items-center gap-2 shadow-sm text-xs uppercase tracking-widest"
            >
              <span className="text-[#5B21B6]">🧠</span> Expert Help
            </button>
            {plan && (
              <button
                onClick={downloadJSON}
                className="px-4 py-2 bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] hover:scale-105 text-white rounded-xl font-black transition-all flex items-center gap-2 shadow-lg shadow-purple-200 text-xs uppercase tracking-widest"
              >
                <span>⬇️</span> Export JSON
              </button>
            )}
            <button
              onClick={resetAll}
              className="px-4 py-2 border border-gray-200 hover:border-red-500/50 hover:text-red-500 text-gray-400 rounded-xl font-black transition-all text-xs uppercase tracking-widest"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Error banner */}
        {lastError && (
          <div className="mb-6 rounded-2xl border border-red-500/30 bg-red-500/5 p-4 animate-in slide-in-from-top-2">
            <div className="flex items-center gap-3">
              <span className="text-red-500">⚠️</span>
              <div className="text-red-400 text-sm font-medium">{lastError}</div>
            </div>
            {lastRequestId && (
              <div className="text-red-500/60 mt-2 text-xs font-mono ml-7">Req ID: {lastRequestId}</div>
            )}
          </div>
        )}

        {/* Input Area */}
        <div className="bg-[#F9F7FF] rounded-[32px] border-2 border-[#5B21B6] p-8 shadow-2xl shadow-purple-50 mb-10 transition-all">
          <textarea
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            rows={4}
            className="w-full bg-transparent text-[#111827] placeholder-gray-400 border-none focus:ring-0 resize-none text-xl font-bold leading-relaxed"
            placeholder="Describe your project idea in detail (e.g., 'A fitness tracking app with AI workout suggestions and social challenges')…"
          />
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-purple-50">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-[2px]">
              <span className="text-[#5B21B6]">Tip:</span> Mention target platforms and core features for better plans.
            </div>
            <button
              onClick={handleBuild}
              disabled={loading || !idea.trim()}
              className={`px-10 py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl ${
                !idea.trim() || loading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white hover:scale-105 active:scale-95 shadow-purple-200"
              }`}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Designing…
                </div>
              ) : (
                "Construct Blueprint"
              )}
            </button>
          </div>
        </div>

        {/* Output Section */}
        <div className="grid gap-8">
          {!plan && raw && (
            <div className="animate-in fade-in duration-500">
              <SectionCard title="AI Thinking Process">
                <pre className="whitespace-pre-wrap text-gray-300 font-mono text-xs bg-black/30 p-4 rounded-xl">
                  {raw}
                </pre>
                {parseErr && <p className="text-red-400 text-xs mt-3 flex items-center gap-1 font-medium"><span>⚠️</span> Structure parsing failed. Showing raw logic.</p>}
              </SectionCard>
            </div>
          )}

          {plan && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <SectionCard title="Project Summary">
                    <p className="text-lg text-gray-700 leading-relaxed font-medium">{plan.summary}</p>
                  </SectionCard>
                </div>
                <SectionCard title="Tech Stack">
                  <div className="space-y-3">
                    {[
                      { label: "Frontend", val: plan.tech_stack?.frontend },
                      { label: "Backend", val: plan.tech_stack?.backend },
                      { label: "Database", val: plan.tech_stack?.database },
                      { label: "Auth", val: plan.tech_stack?.auth },
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between items-center text-sm border-b border-gray-100 pb-2 last:border-0">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="text-primary-purple font-bold">{item.val || "-"}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <SectionCard title="Architecture Vision">
                  {plan.architecture?.diagram_text && (
                    <div className="bg-black/50 p-4 rounded-xl mb-4 border border-gray-800 font-mono text-[10px] leading-tight text-green-500/80 overflow-auto max-h-60">
                      {plan.architecture.diagram_text}
                    </div>
                  )}
                  <ul className="space-y-2">
                    {(plan.architecture?.components || []).map((c, i) => (
                      <li key={i} className="flex gap-3 text-sm text-gray-300">
                        <span className="text-primary-purple">•</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </SectionCard>

                <SectionCard title="Roadmap & Phases">
                  <div className="space-y-6">
                      {["phase_1", "phase_2", "phase_3"].map((ph, idx) => (
                        <div key={idx} className="relative pl-6 border-l border-primary-purple/30">
                          <div className="absolute -left-1.5 top-0 w-3 h-3 rounded-full bg-primary-purple shadow-[0_0_8px_rgba(91,33,182,0.8)]"></div>
                          <h4 className="text-xs font-black text-primary-purple uppercase tracking-widest mb-2">Phase {idx + 1}</h4>
                          <ul className="space-y-1">
                            {(plan.roadmap?.[ph] || []).map((x, i) => (
                              <li key={i} className="text-sm text-gray-600">{x}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                </SectionCard>
              </div>

              <SectionCard title="Implementation Tasks">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-4 px-2 text-xs font-bold text-gray-400 uppercase tracking-wider">ID</th>
                        <th className="py-4 px-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Task Title</th>
                        <th className="py-4 px-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Priority</th>
                        <th className="py-4 px-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Dependencies</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(plan.tasks || []).map((t, i) => (
                        <tr key={i} className="group hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-2 text-sm font-mono text-primary-purple">{t.id}</td>
                          <td className="py-4 px-2">
                            <div className="text-sm font-medium text-gray-800">{t.title}</div>
                            {t.details && <div className="text-xs text-gray-500 mt-1">{t.details}</div>}
                          </td>
                          <td className="py-4 px-2 text-xs">
                            <span className={`px-2 py-1 rounded-full ${
                              t.priority === 'High' ? 'bg-red-50 text-red-600' : 
                              t.priority === 'Medium' ? 'bg-yellow-50 text-yellow-600' : 'bg-blue-50 text-blue-600'
                            }`}>
                              {t.priority}
                            </span>
                          </td>
                          <td className="py-4 px-2 text-xs text-gray-500 font-mono">
                            {Array.isArray(t.depends_on) ? t.depends_on.join(", ") : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              <div className="grid md:grid-cols-2 gap-6">
                <SectionCard title="API Strategy">
                  <div className="space-y-4">
                    {(plan.api_design || []).map((a, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                            a.method === 'GET' ? 'bg-green-100 text-green-600' :
                            a.method === 'POST' ? 'bg-blue-100 text-blue-600' : 'bg-yellow-100 text-yellow-600'
                          }`}>{a.method}</span>
                          <code className="text-sm font-bold text-gray-700">{a.path}</code>
                        </div>
                        {a.request && <div className="text-xs text-gray-500 mb-1 leading-relaxed"><span className="text-gray-400">REQ:</span> {a.request}</div>}
                        {a.response && <div className="text-xs text-gray-500 leading-relaxed"><span className="text-gray-400">RES:</span> {a.response}</div>}
                      </div>
                    ))}
                  </div>
                </SectionCard>

                <SectionCard title="Data Schema">
                  <div className="space-y-4">
                    {(plan.data_model || []).map((e, i) => (
                      <div key={i} className="p-4 bg-gray-50 rounded-xl border border-gray-100 group hover:border-primary-purple/30 transition-colors">
                        <div className="font-bold text-primary-purple mb-2 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          {e.entity}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {e.fields?.map(f => `${f.name}:${f.type}${f.required ?"*" :""}`).join(", ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </div>
          )}
        </div>

        {/* ------------------------- Expert Help Modal (Simplified Overhaul) -------------------------- */}
        {helpOpen && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-5xl bg-[#161b22] border border-primary-purple/30 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-primary-purple/10 to-transparent border-b border-gray-800">
                <div className="flex items-center gap-4">
                  <div className="flex bg-[#1a1a1a] rounded-xl p-1 border border-gray-800">
                    <button
                      onClick={() => setHelpMode("build")}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${helpMode === "build" ? "bg-primary-purple text-white shadow-lg" : "text-gray-500 hover:text-white"}`}
                    >
                      UI Builder
                    </button>
                    <button
                      onClick={() => setHelpMode("fix")}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${helpMode === "fix" ? "bg-primary-purple text-white shadow-lg" : "text-gray-500 hover:text-white"}`}
                    >
                      Fix Issues
                    </button>
                  </div>
                  <h3 className="text-xl font-black text-white ml-2">Expert UI Analysis</h3>
                </div>
                <button className="text-gray-500 hover:text-white transition-colors" onClick={() => setHelpOpen(false)}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l18 18" /></svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Page Route</label>
                        <input
                          className="w-full bg-[#1a1a1a] text-white border border-gray-800 focus:border-primary-purple rounded-xl px-4 py-2.5 text-sm transition-all"
                          placeholder="/dashboard"
                          value={uiRoute}
                          onChange={(e) => setUiRoute(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Fidelity</label>
                        <select
                          className="w-full bg-[#1a1a1a] text-white border border-gray-800 focus:border-primary-purple rounded-xl px-4 py-2.5 text-sm transition-all"
                          value={uiFidelity}
                          onChange={(e) => setUiFidelity(e.target.value)}
                        >
                          <option value="low">Low Wireframe</option>
                          <option value="medium">Standard UI</option>
                          <option value="high">Pixel Perfect</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Description & Specs</label>
                      <textarea
                        className="w-full bg-[#1a1a1a] text-white border border-gray-800 focus:border-primary-purple rounded-xl px-4 py-3 text-sm min-h-[120px] transition-all"
                        placeholder={helpMode === "build" ? "Describe layout, components, and visual style..." : "Paste existing code and describe the visual issues..."}
                        value={helpMode === "build" ? uiDesc : uiIssues}
                        onChange={(e) => helpMode === "build" ? setUiDesc(e.target.value) : setUiIssues(e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Visual Reference (Drag & Drop)</label>
                      <div className="p-1 bg-[#1a1a1a] rounded-2xl border border-gray-800 border-dashed hover:border-primary-purple/50 transition-all">
                        <PasteDropZone images={uiFiles} setImages={setUiFiles} autoFocus />
                      </div>
                    </div>

                    <button
                      onClick={submitExpertHelp}
                      disabled={uiLoading || !canRunHelp}
                      className="w-full bg-primary-purple text-white font-black py-4 rounded-2xl shadow-xl hover:shadow-[0_0_25px_rgba(91,33,182,0.4)] disabled:opacity-40 transition-all flex items-center justify-center gap-3"
                    >
                      {uiLoading ? <span className="flex items-center gap-2"><svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25"/><path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Analyzing...</span> : <span>{helpMode === 'fix' ? 'Analyze & Fix UI' : 'Generate UI Plan'}</span>}
                    </button>
                  </div>

                  <div className="bg-black/30 rounded-3xl border border-gray-800/50 p-6 overflow-y-auto max-h-[600px] custom-scrollbar">
                    {uiResult ? (
                      <div className="space-y-6">
                        {uiResult.error && (
                          <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl text-red-400 text-sm font-medium">
                            {uiResult.error}
                          </div>
                        )}
                        {uiAudit?.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="text-[10px] font-black text-primary-purple uppercase tracking-widest">Expert Audit Feedback</h4>
                            <ul className="space-y-1">
                              {uiAudit.map((a, i) => <li key={i} className="text-xs text-gray-400 flex gap-2"><span>•</span> {a}</li>)}
                            </ul>
                          </div>
                        )}
                        {uiResult.analysis && (
                          <div className="space-y-4 bg-[#1a1a1a] p-4 rounded-2xl border border-gray-800">
                             <h4 className="text-sm font-bold text-white">Analysis Findings</h4>
                             <p className="text-xs text-gray-400 leading-relaxed">{uiResult.analysis.mismatch_summary}</p>
                          </div>
                        )}
                        {uiAssets?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="text-[10px] font-black text-primary-purple uppercase tracking-widest">Optimized Assets</h4>
                            <div className="grid grid-cols-2 gap-3">
                              {uiAssets.map((a, i) => (
                                <div key={i} className="bg-black/40 p-2 rounded-xl border border-gray-800 group">
                                  <img src={a.data_url} className="w-full h-20 object-cover rounded-lg mb-2" />
                                  <button onClick={() => downloadDataUrl(a.name, a.data_url)} className="w-full py-1 text-[10px] font-bold bg-primary-purple text-white rounded-md opacity-0 group-hover:opacity-100 transition-all">Download</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {uiResult.pages?.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="text-[10px] font-black text-primary-purple uppercase tracking-widest">Generated React Code</h4>
                            <CodeTabs files={uiResult.pages} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-40">
                         <div className="w-16 h-16 border-4 border-primary-purple/20 border-t-primary-purple rounded-full animate-spin [animation-duration:3s]"></div>
                         <p className="text-sm font-medium">Awaiting analysis parameters...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



